import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import "../assets/CSS/Dashboard.css";
import "../assets/CSS/SupervisorDepartments.css";

const emptyForm = { supervisorName: "", departmentName: "" };
const errorMessage = (error, fallback) => error.response?.data?.message || fallback;
const normalizeMatch = value => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
const normalizeWorkbookDepartment = value => String(value || "")
  .trim()
  .replace(/\s+/g, " ")
  .toUpperCase()
  .replace(/^OPERATION\s*:\s*/, "")
  .replace(/^[\s\-–—:]+|[\s\-–—:]+$/g, "")
  .replace(/\bCUTING\b/g, "CUTTING")
  .replace(/\s+(?:DEPT|DEPARTMENT)$/, "")
  .replace(/^.*\b(?:PRESSING|GLOWING|GLUING)\b.*$/, "PRESSING")
  .trim();
const escapeHtml = value => String(value || "").replace(/[&<>'"]/g, character => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
}[character]));

function levenshteinDistance(left, right) {
  const a = normalizeMatch(left);
  const b = normalizeMatch(right);
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
}

function getCanonicalValue(value, choices) {
  return choices.find(choice => normalizeMatch(choice) === normalizeMatch(value));
}

function getClosestValue(value, choices) {
  if (!value.trim() || !choices.length) return null;
  return [...choices].sort((a, b) => levenshteinDistance(value, a) - levenshteinDistance(value, b))[0];
}

export default function SupervisorDepartments() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [options, setOptions] = useState({ supervisors: [], departments: [] });
  const [workbookDepartments, setWorkbookDepartments] = useState(new Set());
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [mappingResponse, optionResponse, workbookResponse] = await Promise.all([
        axiosInstance.get("/api/supervisor-departments"),
        axiosInstance.get("/api/supervisor-departments/options"),
        axiosInstance.get("/api/foam-cutting-sharepoint/lots", { params: { allDepartments: 1 } })
          .catch(() => ({ data: { departments: [] } })),
      ]);
      setMappings(mappingResponse.data || []);
      setOptions(optionResponse.data || { supervisors: [], departments: [] });
      setWorkbookDepartments(new Set((workbookResponse.data?.departments || [])
        .map(item => normalizeWorkbookDepartment(item?.department || item))
        .filter(Boolean)));
    } catch (error) {
      console.error("Error loading supervisor departments:", error);
      await Swal.fire({
        icon: "error",
        title: "Unable to Load",
        text: errorMessage(error, "Supervisor departments load nahi ho paye."),
        confirmButtonColor: "#1769aa",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const supervisorGroups = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const filtered = !query ? mappings : mappings.filter((item) =>
      String(item.ID).includes(query)
      || String(item.SupervisorName || "").toLowerCase().includes(query)
      || String(item.DepartmentName || "").toLowerCase().includes(query)
    );

    const groups = filtered.reduce((result, mapping) => {
      const displayName = String(mapping.SupervisorName || "Unassigned").trim() || "Unassigned";
      const key = displayName.toLowerCase();
      if (!result.has(key)) result.set(key, { supervisorName: displayName, mappings: [] });
      result.get(key).mappings.push(mapping);
      return result;
    }, new Map());

    return Array.from(groups.values())
      .map(group => ({
        ...group,
        mappings: [...group.mappings].sort((a, b) =>
          String(a.DepartmentName || "").localeCompare(String(b.DepartmentName || ""))
        ),
      }))
      .sort((a, b) => a.supervisorName.localeCompare(b.supervisorName));
  }, [mappings, searchText]);

  const uniqueSupervisorCount = useMemo(() => new Set(
    mappings.map(item => String(item.SupervisorName || "").trim().toLowerCase()).filter(Boolean)
  ).size, [mappings]);

  const uniqueDepartmentCount = useMemo(() => new Set(
    mappings.map(item => String(item.DepartmentName || "").trim().toLowerCase()).filter(Boolean)
  ).size, [mappings]);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setFieldErrors({});
    setShowModal(true);
  };

  const openEditModal = row => {
    setEditingId(row.ID);
    setFormData({
      supervisorName: row.SupervisorName || "",
      departmentName: row.DepartmentName || "",
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyForm);
    setFieldErrors({});
  };

  const validateMasterField = (field, value) => {
    const isSupervisor = field === "supervisorName";
    const choices = isSupervisor ? options.supervisors : options.departments;
    const label = isSupervisor ? "Supervisor" : "Department";
    if (!value.trim()) return { message: `${label} required hai.`, suggestion: null };
    if (getCanonicalValue(value, choices)) return null;
    const suggestion = getClosestValue(value, choices);
    return {
      suggestion,
      message: suggestion
        ? `${label} "${value.trim()}" approved list me nahi mila. Did you mean "${suggestion}"?`
        : `${label} "${value.trim()}" approved list me nahi mila.`,
    };
  };

  const validateOnBlur = field => {
    setFieldErrors(previous => ({ ...previous, [field]: validateMasterField(field, formData[field]) }));
  };

  const updateField = (field, value) => {
    setFormData(previous => ({ ...previous, [field]: value }));
    setFieldErrors(previous => ({ ...previous, [field]: null }));
  };

  const applySuggestion = (field, suggestion) => {
    updateField(field, suggestion);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const supervisorError = validateMasterField("supervisorName", formData.supervisorName);
    const departmentError = validateMasterField("departmentName", formData.departmentName);
    if (supervisorError || departmentError) {
      setFieldErrors({ supervisorName: supervisorError, departmentName: departmentError });
      await Swal.fire({
        icon: "warning",
        title: "Valid Mapping Required",
        text: (supervisorError || departmentError).message,
        confirmButtonColor: "#1769aa",
      });
      return;
    }

    const payload = {
      supervisorName: getCanonicalValue(formData.supervisorName, options.supervisors),
      departmentName: getCanonicalValue(formData.departmentName, options.departments),
    };

    try {
      setSaving(true);
      const wasEditing = Boolean(editingId);
      if (wasEditing) {
        await axiosInstance.put(`/api/supervisor-departments/${editingId}`, payload);
      } else {
        await axiosInstance.post("/api/supervisor-departments", payload);
      }
      closeModal();
      await loadData();
      await Swal.fire({
        icon: "success",
        title: wasEditing ? "Mapping Updated" : "Mapping Added",
        text: wasEditing
          ? "Supervisor department mapping successfully update ho gayi."
          : "Supervisor department mapping successfully add ho gayi.",
        confirmButtonColor: "#1769aa",
      });
    } catch (error) {
      console.error("Error saving supervisor department:", error);
      const serverError = error.response?.data;
      if (error.response?.status === 422 && serverError?.field) {
        setFieldErrors(previous => ({
          ...previous,
          [serverError.field]: { message: serverError.message, suggestion: serverError.suggestion },
        }));
      }
      await Swal.fire({
        icon: error.response?.status === 409 ? "warning" : "error",
        title: error.response?.status === 409 ? "Duplicate Mapping" : "Save Failed",
        text: errorMessage(error, "Mapping save nahi ho payi."),
        confirmButtonColor: "#1769aa",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async row => {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Delete this mapping?",
      html: `
        <div style="font-size:14px;color:#536174">
          <strong style="color:#182236">${escapeHtml(row.SupervisorName)}</strong>
          <span style="margin:0 7px;color:#9aa3b2">→</span>
          <strong style="color:#182236">${escapeHtml(row.DepartmentName)}</strong>
          <p style="margin:14px 0 0">This mapping may be used for RED support and WIP supervisor routing.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '<i class="bi bi-trash3 me-1"></i> Yes, delete',
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
      focusCancel: true,
    });
    if (!confirmation.isConfirmed) return;

    try {
      await axiosInstance.delete(`/api/supervisor-departments/${row.ID}`);
      await loadData();
      await Swal.fire({
        icon: "success",
        title: "Mapping Deleted",
        text: `${row.SupervisorName} - ${row.DepartmentName} mapping remove ho gayi.`,
        confirmButtonColor: "#1769aa",
      });
    } catch (error) {
      console.error("Error deleting supervisor department:", error);
      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: errorMessage(error, "Mapping delete nahi ho payi."),
        confirmButtonColor: "#1769aa",
      });
    }
  };

  return (
    <div className="d-flex dashboard supervisor-departments-page">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header
          toggleSidebar={() => setIsSidebarVisible(previous => !previous)}
          isSidebarVisible={isSidebarVisible}
        />

        <main className="main-container p-4 supervisor-departments-content">
          <section className="supervisor-hero mb-4">
            <div className="supervisor-hero-copy">
              <span className="supervisor-eyebrow">WIP MASTER</span>
              <h2>Supervisor Departments</h2>
              <p>Ek hi jagah se supervisor ownership aur department routing manage karein.</p>
            </div>
            <button className="btn supervisor-add-button" onClick={openAddModal}>
              <i className="bi bi-plus-lg" />
              <span>Add Mapping</span>
            </button>
          </section>

          <div className="supervisor-summary-grid mb-4">
            <div className="supervisor-summary-card supervisor-summary-primary">
              <span className="supervisor-summary-icon"><i className="bi bi-people" /></span>
              <div>
                <span className="supervisor-summary-label">Supervisors</span>
                <strong>{uniqueSupervisorCount}</strong>
              </div>
            </div>
            <div className="supervisor-summary-card">
              <span className="supervisor-summary-icon"><i className="bi bi-diagram-3" /></span>
              <div>
                <span className="supervisor-summary-label">Departments</span>
                <strong>{uniqueDepartmentCount}</strong>
              </div>
            </div>
            <div className="supervisor-summary-card">
              <span className="supervisor-summary-icon"><i className="bi bi-link-45deg" /></span>
              <div>
                <span className="supervisor-summary-label">Total Mappings</span>
                <strong>{mappings.length}</strong>
              </div>
            </div>
          </div>

          <section className="supervisor-directory">
            <div className="supervisor-directory-toolbar">
              <div>
                <h5>Supervisor Directory</h5>
                <p>{supervisorGroups.length} supervisor group{supervisorGroups.length === 1 ? "" : "s"} shown</p>
              </div>
              <div className="supervisor-search">
                <i className="bi bi-search" />
                <input
                  type="search"
                  placeholder="Search supervisor or department..."
                  value={searchText}
                  onChange={event => setSearchText(event.target.value)}
                  aria-label="Search supervisor departments"
                />
                {searchText && (
                  <button type="button" onClick={() => setSearchText("")} aria-label="Clear search">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>

            <div className="supervisor-groups">
              {loading ? (
                <div className="supervisor-state">
                  <div className="spinner-border text-primary" role="status" />
                  <span>Supervisor mappings load ho rahi hain...</span>
                </div>
              ) : supervisorGroups.length === 0 ? (
                <div className="supervisor-state">
                  <span className="supervisor-empty-icon"><i className="bi bi-person-x" /></span>
                  <strong>Koi mapping nahi mili</strong>
                  <span>Search change karein ya nayi mapping add karein.</span>
                </div>
              ) : supervisorGroups.map((group, groupIndex) => (
                <article className="supervisor-group-card" key={group.supervisorName}>
                  <header className="supervisor-group-header">
                    <div className="supervisor-identity">
                      <span className={`supervisor-avatar avatar-${groupIndex % 5}`}>
                        {group.supervisorName.charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <h4>{group.supervisorName}</h4>
                        <span>{group.mappings.length} department{group.mappings.length === 1 ? "" : "s"} assigned</span>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm supervisor-quick-add"
                      onClick={() => {
                        setEditingId(null);
                        setFormData({ supervisorName: group.supervisorName, departmentName: "" });
                        setFieldErrors({});
                        setShowModal(true);
                      }}
                    >
                      <i className="bi bi-plus" /> Add department
                    </button>
                  </header>

                  <div className="supervisor-department-list">
                    {group.mappings.map(mapping => (
                      <div className="supervisor-department-row" key={mapping.ID}>
                        <div className="supervisor-department-info">
                          <span className="supervisor-department-icon"><i className="bi bi-building" /></span>
                          <div>
                            <strong>{mapping.DepartmentName || "Unnamed Department"}</strong>
                            <span>Mapping ID #{mapping.ID}</span>
                          </div>
                        </div>
                        <div className="supervisor-row-actions">
                          {workbookDepartments.has(normalizeWorkbookDepartment(mapping.DepartmentName)) && (
                            <button type="button" onClick={() => { window.location.href = `/supervisor-cutting-lot-details?department=${encodeURIComponent(mapping.DepartmentName)}`; }} title="Open cutting lots" aria-label={`Open ${mapping.DepartmentName} cutting lots`}>
                              <i className="bi bi-scissors" />
                            </button>
                          )}
                          <button type="button" onClick={() => openEditModal(mapping)} title="Edit mapping" aria-label={`Edit ${mapping.DepartmentName}`}>
                            <i className="bi bi-pencil-square" />
                          </button>
                          <button className="danger" type="button" onClick={() => handleDelete(mapping)} title="Delete mapping" aria-label={`Delete ${mapping.DepartmentName}`}>
                            <i className="bi bi-trash3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>

      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <form className="modal-content supervisor-mapping-modal" onSubmit={handleSubmit}>
                <div className="modal-header">
                  <div className="d-flex align-items-center gap-3">
                    <span className="supervisor-modal-icon"><i className={`bi ${editingId ? "bi-pencil-square" : "bi-person-plus"}`} /></span>
                    <div>
                      <h5 className="modal-title">{editingId ? "Edit Mapping" : "Add Mapping"}</h5>
                      <small>{editingId ? "Supervisor ownership update karein" : "Supervisor ko department assign karein"}</small>
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={closeModal} aria-label="Close" disabled={saving} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" htmlFor="supervisorName">Supervisor Name</label>
                    <input
                      id="supervisorName"
                      list="supervisor-options"
                      className={`form-control ${fieldErrors.supervisorName ? "is-invalid" : ""}`}
                      maxLength={100}
                      autoFocus
                      required
                      placeholder="Select or type supervisor name"
                      value={formData.supervisorName}
                      onChange={event => updateField("supervisorName", event.target.value)}
                      onBlur={() => validateOnBlur("supervisorName")}
                      aria-describedby={fieldErrors.supervisorName ? "supervisorNameError" : undefined}
                    />
                    <datalist id="supervisor-options">
                      {options.supervisors.map(name => <option value={name} key={name} />)}
                    </datalist>
                    <small className="form-text text-muted">Sirf active Supervisor/Admin names allowed hain.</small>
                    {fieldErrors.supervisorName && (
                      <div className="supervisor-field-error" id="supervisorNameError">
                        <span><i className="bi bi-exclamation-circle me-1" />{fieldErrors.supervisorName.message}</span>
                        {fieldErrors.supervisorName.suggestion && (
                          <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => applySuggestion("supervisorName", fieldErrors.supervisorName.suggestion)}>
                            Use {fieldErrors.supervisorName.suggestion}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="form-label fw-semibold" htmlFor="departmentName">Department Name</label>
                    <input
                      id="departmentName"
                      list="department-options"
                      className={`form-control ${fieldErrors.departmentName ? "is-invalid" : ""}`}
                      maxLength={100}
                      required
                      placeholder="Select or type department name"
                      value={formData.departmentName}
                      onChange={event => updateField("departmentName", event.target.value)}
                      onBlur={() => validateOnBlur("departmentName")}
                      aria-describedby={fieldErrors.departmentName ? "departmentNameError" : undefined}
                    />
                    <datalist id="department-options">
                      {options.departments.map(name => <option value={name} key={name} />)}
                    </datalist>
                    <small className="form-text text-muted">Approved list se exact department name select karein.</small>
                    {fieldErrors.departmentName && (
                      <div className="supervisor-field-error" id="departmentNameError">
                        <span><i className="bi bi-exclamation-circle me-1" />{fieldErrors.departmentName.message}</span>
                        {fieldErrors.departmentName.suggestion && (
                          <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => applySuggestion("departmentName", fieldErrors.departmentName.suggestion)}>
                            Use {fieldErrors.departmentName.suggestion}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closeModal} disabled={saving}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving && <span className="spinner-border spinner-border-sm me-2" role="status" />}
                    {editingId ? "Save Changes" : "Add Mapping"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
